import { Product, ProductCategories } from '../models/products';
import { getDurationStr } from './common';
import { formatDurationForUi } from './durationFormat';

/** Explicit per-product ratings; override the pseudo-rating below. */
const RATING_OVERRIDES: Record<string, string> = {
    P93AEL: '4.9', // Reef Hopper
};

/** Stable pseudo-rating in [4.7, 4.9] per product (no live review feed). */
export function gridCardRating(productCode: string): string {
    const override = RATING_OVERRIDES[productCode];
    if (override) return override;
    let h = 0;
    for (let i = 0; i < productCode.length; i += 1) {
        h = Math.imul(31, h) + productCode.charCodeAt(i);
    }
    const u = (Math.abs(h) % 1001) / 1000;
    return (4.7 + u * 0.2).toFixed(1);
}

/** @see formatDurationForUi in durationFormat.ts */
export function formatDuration(minutes?: number | string | null): string | null {
    return formatDurationForUi(minutes);
}

/** Cards / hero duration pill: `displayDuration` from catalog wins; else formatted Rezdy minutes. */
export function formatProductDuration(product: Product | undefined): string | null {
    if (!product) return null;
    const custom = product.displayDuration?.trim();
    if (custom) return custom;
    return formatDurationForUi(product.duration);
}

/** Product detail duration line: custom copy, else long breakdown from Rezdy minutes. */
export function formatProductDurationDetail(product: Product | undefined): string {
    if (!product) return 'No Set Duration';
    const custom = product.displayDuration?.trim();
    if (custom) return custom;
    if (product.duration) return getDurationStr(product.duration);
    return 'No Set Duration';
}

export function categoryLabel(category?: ProductCategories): string {
    switch (category) {
        case ProductCategories.CAIRNS:
            return 'Cairns';
        case ProductCategories.WATER:
            return 'Water';
        case ProductCategories.AIR:
            return 'Air';
        case ProductCategories.LAND:
            return 'Land';
        case ProductCategories.PACKAGES:
        case ProductCategories.OVERNIGHT:
            return 'Packages';
        default:
            return 'Queensland';
    }
}

export function promoBadgeLabel(product: Product): string | null {
    if (product.tags?.includes('popular')) return 'POPULAR';
    if (product.tags?.includes('package')) return 'BEST VALUE';
    if (product.saleTips?.length) return product.saleTips[0].toUpperCase();
    return null;
}

/** Single promo chip for hero / card top-left: sale → package best value → popular → first saleTip. */
export type HeroCardTopChip =
    | { readonly kind: 'on_sale' }
    | { readonly kind: 'popular' }
    | { readonly kind: 'best_value' }
    | { readonly kind: 'sale_tip'; readonly text: string };

export function heroCardTopChip(
    product: Product | undefined,
    salePriceActive: number | null | undefined
): HeroCardTopChip | null {
    if (!product) return null;
    if (salePriceActive != null) return { kind: 'on_sale' };
    if (product.tags?.includes('package')) return { kind: 'best_value' };
    if (product.tags?.includes('popular')) return { kind: 'popular' };
    const tip = product.saleTips?.[0]?.trim();
    if (tip) return { kind: 'sale_tip', text: tip };
    return null;
}
